import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Badge,
  Image,
  Divider,
  Spinner,
  SimpleGrid,
  Button,
  useColorModeValue,
  Icon,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import { FiClock, FiCalendar, FiChevronLeft, FiMaximize, FiPlayCircle, FiFileText } from "react-icons/fi";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { useAuth } from "../../contexts/AuthContext";
import { format } from "date-fns";
import { Document, Page } from "react-pdf";

// Query to get project details with tasks and their media
const GET_PROJECT_TIMELINE = gql`
  query GetProjectTimeline($id: ID!) {
    project(id: $id) {
      id
      projectName
      projectGoal
      createdAt
      tasks {
        id
        description
        status
        order
        createdAt
        updatedAt
        media {
          url
          description
          fileType
        }
      }
    }
  }
`;

// Helper function to format dates
const formatDate = (dateString: string) => {
  try {
    console.log(`Attempting to format date: ${dateString}`);
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.log(`Invalid date created from: ${dateString}`);
      return dateString;
    }
    
    console.log(`Parsed date: ${date.toString()}`);
    return format(date, "MMMM d, yyyy, h:mm a");
  } catch (error) {
    console.error(`Error formatting date: ${dateString}`, error);
    return dateString;
  }
};

// Interface for timeline item
interface TimelineItem {
  id: string;
  type: "task" | "media";
  taskId?: string;
  taskDescription?: string;
  taskStatus?: string;
  url?: string;
  fileType?: string;
  description?: string;
  createdAt: string;
}

const TimelineView = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, user } = useAuth();
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  
  // For modal image/video viewing
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedMedia, setSelectedMedia] = useState<{ url: string; type: string } | null>(null);
  
  // For lazy loading
  const [visibleItems, setVisibleItems] = useState<number>(10);
  const loaderRef = useRef<HTMLDivElement>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);
  
  // Timeline items state
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  
  // GraphQL query
  const { loading, error, data } = useQuery(GET_PROJECT_TIMELINE, {
    variables: { id },
    skip: !isAuthenticated,
    onCompleted: (receivedData) => {
      console.log("GraphQL query completed, data:", receivedData);
      if (receivedData?.project?.tasks) {
        console.log(`Project has ${receivedData.project.tasks.length} tasks`);
      } else {
        console.log("No tasks found in the response or tasks array is empty");
      }
    },
    onError: (err) => {
      console.error("GraphQL query error:", err.message);
    }
  });
  
  // Process data into chronological timeline
  useEffect(() => {
    if (data?.project) {
      console.log("Project data received:", data.project);
      console.log("Tasks array:", data.project.tasks);
      
      const items: TimelineItem[] = [];
      
      // Add tasks to timeline
      if (Array.isArray(data.project.tasks)) {
        data.project.tasks.forEach((task: any) => {
          console.log("Processing task:", task);
          
          // Skip adding task creation items - we only want media/evidence
          
          // Add task media
          if (task && task.id && task.media && Array.isArray(task.media) && task.media.length > 0) {
            console.log(`Task ${task.id} has ${task.media.length} media items`);
            task.media.forEach((media: any, index: number) => {
              if (media && media.url) {
                items.push({
                  id: `media-${task.id}-${index}`,
                  type: "media",
                  taskId: task.id,
                  taskDescription: task.description || "Unknown task",
                  url: media.url,
                  fileType: media.fileType || "image/jpeg",
                  description: media.description || "",
                  // Media items don't have createdAt in our schema, so use task's updatedAt
                  createdAt: task.updatedAt || task.createdAt || data.project.createdAt || new Date().toISOString(),
                });
              }
            });
          }
        });
      } else {
        console.warn("Project tasks is not an array:", data.project.tasks);
      }
      
      // Sort by date
      items.sort((a, b) => {
        // Safely parse dates, using current time as fallback
        const getTime = (dateStr: string): number => {
          try {
            const date = new Date(dateStr);
            return isNaN(date.getTime()) ? Date.now() : date.getTime();
          } catch (e) {
            return Date.now();
          }
        };
        
        const dateA = getTime(a.createdAt);
        const dateB = getTime(b.createdAt);
        return dateB - dateA; // Most recent first
      });
      
      console.log("Final timeline items:", items);
      
      setTimelineItems(items);
      setHasMore(items.length > visibleItems);
    } else {
      console.log("No project data received or project is undefined");
    }
  }, [data, visibleItems]);
  
  // Intersection observer for lazy loading
  const observer = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useCallback((node: HTMLDivElement) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setVisibleItems(prev => prev + 10);
      }
    });
    
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);
  
  // Handle media click
  const handleMediaClick = (url: string, fileType: string) => {
    setSelectedMedia({ url, type: fileType });
    onOpen();
  };
  
  if (loading) {
    return (
      <>
        <NavbarWithCallToAction />
        <Box bg={bgColor} minH="100vh" pt={10} pb={20}>
          <Container maxW="container.xl">
            <VStack spacing={8} align="center">
              <Spinner size="xl" color="blue.500" thickness="4px" />
              <Text>Loading project timeline...</Text>
            </VStack>
          </Container>
        </Box>
        <FooterWithFourColumns />
      </>
    );
  }
  
  if (error) {
    return (
      <>
        <NavbarWithCallToAction />
        <Box bg={bgColor} minH="100vh" pt={10} pb={20}>
          <Container maxW="container.xl">
            <VStack spacing={6} align="start">
              <Heading size="lg" color="red.500">Error Loading Timeline</Heading>
              <Text>{error.message}</Text>
              <Button as={Link} to={`/project/${id}`} colorScheme="blue">
                <Icon as={FiChevronLeft} mr={2} />
                Back to Project
              </Button>
            </VStack>
          </Container>
        </Box>
        <FooterWithFourColumns />
      </>
    );
  }
  
  if (!data || !data.project) {
    return (
      <>
        <NavbarWithCallToAction />
        <Box bg={bgColor} minH="100vh" pt={10} pb={20}>
          <Container maxW="container.xl">
            <VStack spacing={6} align="start">
              <Heading size="lg">Project Not Found</Heading>
              <Button as={Link} to="/projects" colorScheme="blue">
                <Icon as={FiChevronLeft} mr={2} />
                Back to Projects
              </Button>
            </VStack>
          </Container>
        </Box>
        <FooterWithFourColumns />
      </>
    );
  }
  
  return (
    <>
      <NavbarWithCallToAction />
      <Box bg={bgColor} minH="100vh" pt={10} pb={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <HStack justify="space-between" wrap="wrap" gap={4}>
              <VStack align="start" spacing={1}>
                <Heading size="xl">{data.project.projectName} - Evidence Timeline</Heading>
                <Text color="gray.500">
                  Project started on {formatDate(data.project.createdAt)}
                </Text>
              </VStack>
              <Button as={Link} to={`/project/${id}`} colorScheme="blue" variant="outline">
                <Icon as={FiChevronLeft} mr={2} />
                Back to Project
              </Button>
            </HStack>
            
            <Divider />

            <Box p={4} bg="blue.50" borderRadius="md" mb={6}>
              <Text>
                This timeline shows all evidence files uploaded for this project. Media uploads are displayed in chronological order. Scroll down to load more items.
              </Text>
            </Box>
            
            {/* Timeline content */}
            <VStack spacing={8} align="stretch" position="relative">
              {/* Timeline line */}
              <Box
                position="absolute"
                left="24px"
                top={0}
                bottom={0}
                width="2px"
                bg="blue.400"
                zIndex={1}
              />
              
              {timelineItems.slice(0, visibleItems).map((item, index) => (
                <Box
                  key={item.id}
                  ref={index === visibleItems - 1 ? lastItemRef : null}
                  position="relative"
                  pl={{ base: 10, md: 16 }}
                  ml={2}
                >
                  {/* Timeline dot */}
                  <Box
                    position="absolute"
                    left={0}
                    top={0}
                    width="12px"
                    height="12px"
                    borderRadius="full"
                    bg="purple.500"
                    border="2px solid"
                    borderColor="purple.200"
                    zIndex={2}
                    mt={2}
                  />
                  
                  {/* Timeline content */}
                  <VStack align="start" spacing={3}>
                    {/* Date indicator */}
                    <HStack>
                      <Icon as={FiCalendar} color="gray.500" />
                      <Text fontSize="sm" color="gray.500">
                        {formatDate(item.createdAt)}
                      </Text>
                    </HStack>
                    
                    {/* Content card */}
                    <Box
                      bg={cardBg}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={borderColor}
                      p={4}
                      shadow="md"
                      width="full"
                    >
                      {item.type === "task" ? (
                        // Task item - should never show now
                        <VStack align="start" spacing={2}>
                          <Badge colorScheme="green" px={2} py={1}>Task Created</Badge>
                          <Text fontSize="lg" fontWeight="bold">{item.taskDescription}</Text>
                          <Badge
                            colorScheme={
                              item.taskStatus === "COMPLETED" ? "green" :
                              item.taskStatus === "IN_PROGRESS" ? "yellow" : "gray"
                            }
                          >
                            {item.taskStatus === "COMPLETED" ? "Completed" :
                             item.taskStatus === "IN_PROGRESS" ? "In Progress" : "Pending"}
                          </Badge>
                        </VStack>
                      ) : (
                        // Media item
                        <VStack align="start" spacing={3}>
                          <HStack>
                            <Badge colorScheme="purple" px={2} py={1}>
                              Evidence File
                            </Badge>
                            <Text fontSize="sm" color="gray.500">
                              for task: {item.taskDescription}
                            </Text>
                          </HStack>
                          
                          {item.description && (
                            <Text>{item.description}</Text>
                          )}
                          
                          {/* Media preview */}
                          <Box width="full" position="relative">
                            {item.url && (
                              <>
                                {(item.fileType === "application/pdf" || item.url.toLowerCase().includes("pdf")) ? (
                                  // PDF preview
                                  <Box 
                                    borderRadius="md" 
                                    overflow="hidden" 
                                    border="1px solid" 
                                    borderColor="gray.200"
                                    cursor="pointer"
                                    onClick={() => window.open(item.url, "_blank")}
                                  >
                                    <HStack p={4} bg="gray.50">
                                      <Icon as={FiFileText} boxSize={8} color="red.500" />
                                      <VStack align="start" spacing={0}>
                                        <Text fontWeight="bold">PDF Document</Text>
                                        <Text fontSize="sm">Click to open</Text>
                                      </VStack>
                                    </HStack>
                                  </Box>
                                ) : (item.fileType === "video/mp4" || item.url.toLowerCase().includes(".mp4")) ? (
                                  // Video preview
                                  <Box position="relative" borderRadius="md" overflow="hidden">
                                    <Box 
                                      position="absolute"
                                      top={0}
                                      right={0}
                                      bottom={0}
                                      left={0}
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      bg="blackAlpha.300"
                                      zIndex={1}
                                      onClick={() => handleMediaClick(item.url!, item.fileType!)}
                                      cursor="pointer"
                                    >
                                      <Icon as={FiPlayCircle} boxSize={12} color="white" />
                                    </Box>
                                    <video
                                      src={item.url}
                                      style={{ 
                                        width: "100%", 
                                        maxHeight: "300px", 
                                        objectFit: "cover",
                                        borderRadius: "md"
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  // Image preview
                                  <Box 
                                    position="relative" 
                                    borderRadius="md" 
                                    overflow="hidden"
                                    maxH="300px"
                                  >
                                    <Image
                                      src={item.url}
                                      alt={item.description || "Project evidence"}
                                      width="100%"
                                      maxH="300px"
                                      objectFit="cover"
                                      borderRadius="md"
                                      onClick={() => handleMediaClick(item.url!, item.fileType || "image")}
                                      cursor="pointer"
                                    />
                                    <IconButton
                                      aria-label="View full size"
                                      icon={<FiMaximize />}
                                      size="sm"
                                      position="absolute"
                                      bottom={2}
                                      right={2}
                                      onClick={() => handleMediaClick(item.url!, item.fileType || "image")}
                                    />
                                  </Box>
                                )}
                              </>
                            )}
                          </Box>
                        </VStack>
                      )}
                    </Box>
                  </VStack>
                </Box>
              ))}
              
              {/* Loader at the bottom */}
              {hasMore && (
                <Box ref={loaderRef} textAlign="center" py={8}>
                  <Spinner size="md" />
                </Box>
              )}
              
              {!hasMore && timelineItems.length > 0 && (
                <Box textAlign="center" py={8}>
                  <Text color="gray.500">End of timeline</Text>
                </Box>
              )}
              
              {timelineItems.length === 0 && (
                <Box textAlign="center" py={12}>
                  <Heading size="md">No evidence files available</Heading>
                  <Text mt={2} color="gray.500">This project doesn&apos;t have any uploaded evidence files yet.</Text>
                </Box>
              )}
            </VStack>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
      
      {/* Media modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none" maxW="90vw">
          <ModalCloseButton color="white" bg="blackAlpha.600" _hover={{ bg: "blackAlpha.800" }} />
          <ModalBody p={0}>
            {selectedMedia && (
              selectedMedia.type === "video/mp4" || selectedMedia.url.toLowerCase().includes(".mp4") ? (
                <Box maxH="90vh" display="flex" justifyContent="center" alignItems="center">
                  <video
                    src={selectedMedia.url}
                    controls
                    autoPlay
                    style={{ maxHeight: "90vh", maxWidth: "100%", margin: "0 auto" }}
                  />
                </Box>
              ) : (
                <Image
                  src={selectedMedia.url}
                  alt="Full size view"
                  maxH="90vh"
                  margin="0 auto"
                  objectFit="contain"
                />
              )
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default TimelineView; 